import { useRef } from "react";

// Type
import { BubbleSerie, LD5Chart } from "store/slice/charts/chartsSlice.types";
import { TMoreOptions } from "components/Projection/ProjectionBody/chartsListConfig";
import theme from "assets/styles/themes";

interface SVGConfigElement {
    config: any;
    svg: Highcharts.SVGElement;
}
interface ArchetypeLocation {
    circle: SVGConfigElement;
    label: SVGConfigElement;
}
interface AxesTitles {
    label: SVGConfigElement;
}

export const RADIUS_PROPORTION = 0.12;

const useBalanceWatermark = (chartCircles: boolean) => {
    const archetypeLocations = useRef<ArchetypeLocation[]>([]);
    const axesTitles = useRef<AxesTitles[]>([]);

    const renderArchetypes = (
        chart: Highcharts.Chart,
        chartData: LD5Chart,
        moreOptions?: TMoreOptions,
        isExporting?: boolean
    ) => {
        const renderer = chart.renderer as Highcharts.SVGRenderer;
        const bubbleSeries = chartData.series.find((s) => s.type === "bubble") as BubbleSerie | undefined;

        let radius = Math.min(chart.plotWidth, chart.chartHeight) * RADIUS_PROPORTION;

        if (bubbleSeries?.zMax) {
            const maxSizeOpt = bubbleSeries.zMax;
            radius = (maxSizeOpt / 100) * (Math.min(chart.plotWidth, chart.chartHeight) * RADIUS_PROPORTION);
        }

        // Export Archetypes
        if (isExporting) {
            const renderer = chart.renderer as Highcharts.SVGRenderer;

            archetypeLocations.current.forEach((archetype) => {
                // Render circle
                renderer
                    .circle(...archetype.circle.config.args)
                    .attr(archetype.circle.config.attr as Highcharts.SVGElement)
                    .add();

                // Render label
                renderer
                    .label(...(archetype.label.config.args as [string, number, number]))
                    .attr(archetype.label.config.attr as Highcharts.SVGElement)
                    .css(archetype.label.config.css)
                    .add();
            });

            return;
        }

        if (archetypeLocations.current.length) {
            archetypeLocations.current.forEach((el) => {
                Object.values(el).forEach((el) => el.svg.destroy());
            });
        }

        // Render Archetypes
        chartData.watermarks.forEach((data, i) => {
            archetypeLocations.current[i] = {
                circle: {} as SVGConfigElement,
                label: {} as SVGConfigElement,
            };

            // Circle
            const circleConfig = {
                args: [chart.xAxis[0].toPixels(data.x, true), chart.yAxis[0].toPixels(data.y, true), radius],
                attr: {
                    fill: theme.palette.others.balanceChartArchetype,
                    visibility: chartCircles ? "visible" : "hidden",
                    "stroke-width": 0,
                },
            };
            archetypeLocations.current[i].circle = {
                config: circleConfig,
                svg: renderer
                    .circle(...circleConfig.args)
                    .attr(circleConfig.attr)
                    .add(),
            };

            // Label
            const labelConfig = {
                args: [data.label, 0, 0],
                attr: { zIndex: 1, visibility: chartCircles ? "visible" : "hidden" },
                css: {
                    color: theme.palette.common.white,
                    opacity: 0.75,
                    fontFamily: "Lato, sans-serif",
                    fontSize: `${radius / 4}px`,
                    fontWeight: "bolder",
                },
            };
            archetypeLocations.current[i].label = {
                config: labelConfig,
                svg: renderer
                    .label(...(labelConfig.args as [string, number, number]))
                    .attr(labelConfig.attr)
                    .css(labelConfig.css)
                    .add(),
            };

            // Adjust label position
            const { circle, label } = archetypeLocations.current[i];
            const middleLabels = ["Marketing", "Leadership", "HR"].includes(data.label);
            const center = circle.svg.getBBox().y + circle.svg.getBBox().height / 2 - label.svg.getBBox().height / 2;

            const labelX = circle.svg.getBBox().x + circle.svg.getBBox().width / 2 - label.svg.getBBox().width / 2;
            const labelY = middleLabels ? center - 13 : center;

            archetypeLocations.current[i].label.config = {
                ...archetypeLocations.current[i].label.config,
                args: [archetypeLocations.current[i].label.config.args[0], labelX, labelY],
            };
            archetypeLocations.current[i].label.svg.attr({
                x: labelX,
                y: labelY,
            });
        });
    };

    const toggleShowArchetypeVisibility = (showArchetypes: boolean) => {
        const visibility = showArchetypes ? "visible" : "hidden";
        if (archetypeLocations.current.length) {
            archetypeLocations.current.forEach((el) => {
                el.circle.svg.attr({ visibility });
                el.label.svg.attr({ visibility });
            });
        }
    };

    const renderAxesTitles = (
        chart: Highcharts.Chart,
        chartData: LD5Chart,
        moreOptions?: TMoreOptions,
        isExporting?: boolean
    ) => {
        const renderer = chart.renderer as Highcharts.SVGRenderer;
        const titles = Object.values(chartData.titles).flat();

        if (isExporting) {
            axesTitles.current.forEach((el) => {
                renderer
                    .label(...(el.label.config.args as [string, number, number]))
                    .css(el.label.config.css as Highcharts.CSSObject)
                    .attr(el.label.config.attr)
                    // @ts-ignore
                    .add();
            });
            return;
        }

        if (axesTitles.current.length) {
            axesTitles.current.forEach((el) => {
                el.label.svg.destroy();
            });
        }

        titles.forEach((labelString, i) => {
            axesTitles.current[i] = { label: {} as SVGConfigElement };

            // Label
            const labelConfig = {
                args: [labelString, 0, 0],
                attr: {
                    fill: "transparent",
                    zIndex: 1,
                    rotation: i !== 1 ? -90 : 90,
                },
                css: {
                    color: theme.palette.others.greyBlue,
                    fontFamily: "Lato, sans-serif",
                    fontSize: "10px",
                    fontWeight: "bold",
                },
            };

            axesTitles.current[i].label = {
                config: labelConfig,
                svg: renderer
                    .label(...(labelConfig.args as [string, number, number]))
                    .attr(labelConfig.attr)
                    .css(labelConfig.css)
                    .add(),
            };

            let coors: number[] = [];
            const labelSVG = axesTitles.current[i].label.svg;

            // Creating Value
            if (i === 0) {
                coors = [
                    chart.xAxis[0].toPixels(-136, true),
                    chart.yAxis[0].toPixels(0, true) + labelSVG.getBBox().width / 2,
                ];
            }
            // Measuring Value
            if (i === 1) {
                coors = [
                    chart.xAxis[0].toPixels(136, true),
                    chart.yAxis[0].toPixels(0, true) - labelSVG.getBBox().width / 2,
                ];
            }
            // Strategic
            if (i === 2) {
                coors = [chart.xAxis[0].toPixels(3, true) - labelSVG.getBBox().height / 2, labelSVG.getBBox().width];
            }
            // Tactical
            if (i === 3) {
                coors = [
                    chart.xAxis[0].toPixels(3, true) - labelSVG.getBBox().height / 2,
                    chart.yAxis[0].toPixels(-138, true),
                ];
            }

            // Adjust label position
            axesTitles.current[i].label.config = {
                ...axesTitles.current[i].label.config,
                args: [axesTitles.current[i].label.config.args[0], coors[0], coors[1]],
            };
            axesTitles.current[i].label.svg.attr({ x: coors[0], y: coors[1] });
        });
    };

    return { renderArchetypes, toggleShowArchetypeVisibility, renderAxesTitles };
};

export default useBalanceWatermark;
